import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "./Button";
import AppDownload from "./AppDownload";
import PictureImage from "./PictureImage";
import { SquiggleUnderline, PromiseDoodle, handwritten } from "./editorial";
const heroBg = "/hero-friends.jpg";

const Hero: React.FC = () => {
  const scrollToHappenings = () => {
    const el = document.getElementById("happenings-strip");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="relative w-full min-h-screen flex items-center px-6 md:px-12 pt-32 pb-20 overflow-hidden">
      <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-14 lg:gap-16 items-center">
        {/* ── LEFT · editorial type block ───────────────────────── */}
        <div className="lg:col-span-7 relative">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl text-salmon mb-3 -rotate-2 origin-left"
            style={handwritten}
          >
            plans, not posts
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif text-ink tracking-tight leading-[0.9] text-5xl md:text-7xl lg:text-8xl mb-8"
          >
            Connect with people
            <br />
            and spontaneous{" "}
            <span className="relative inline-block">
              <span className="italic text-salmon">occasions.</span>
              <SquiggleUnderline />
            </span>
          </motion.h1>

          <div className="relative max-w-xl mb-9">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg md:text-2xl text-gray-700 leading-relaxed"
            >
              Post a spontaneous plan (a coffee, a walk, a 6:30 kickabout) and
              watch people nearby loop in and show up. Real plans, real people.
              Free to join.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="flex flex-col sm:flex-row gap-5 items-start sm:items-center"
          >
            <Button
              variant="primary"
              size="lg"
              isMagnetic={false}
              className="h-16 px-10 text-lg rounded-full"
              onClick={scrollToHappenings}
            >
              See what&rsquo;s happening
            </Button>
            <Link
              to="/how-it-works#create"
              className="text-ink/70 font-semibold border-b-2 border-ink/20 hover:border-salmon hover:text-salmon transition-colors text-base"
            >
              Or start one &rarr;
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-10"
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-ink/70 mb-3">
              Or get the app
            </p>
            <AppDownload showQr showTrust placement="hero" />
          </motion.div>
        </div>

        {/* ── RIGHT · framed editorial photo ────────────────────── */}
        <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 6 }}
            animate={{ opacity: 1, y: 0, rotate: 2 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="relative w-full max-w-md transform-gpu"
          >
            <div className="polaroid-frame p-3 pb-12 border shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)]">
              <div className="aspect-[4/5] overflow-hidden">
                <PictureImage
                  src={heroBg}
                  alt="Diverse group of friends laughing together outdoors"
                  width={1080}
                  height={720}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 20%" }}
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
              <p
                className="mt-3 text-center text-2xl text-ink"
                style={handwritten}
              >
                your people
              </p>
            </div>

            {/* hand-drawn accent */}
            <div className="absolute -top-7 -left-7 hidden md:block -rotate-12">
              <PromiseDoodle index={2} />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ink/40 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-widest">Scroll</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-ink/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
